import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { AtSign, FileText, Film, Hash, Music, Type } from "lucide-react";
import type { TimelineProject, TimelineTextOverlay } from "@scroll/shared-types/timeline";
import { createProjectFromVideo, timeToClip } from "@scroll/shared-types/timeline-utils";
import { getTotalDuration } from "@scroll/shared-types/timeline-utils";
import { createTextOverlay, type OverlayItem } from "@scroll/shared-types/overlays";
import { useTimelineEditor, type TimelineAutosaveOptions } from "./useTimelineEditor";
import { useTimelineCapabilities } from "@/lib/timeline-capabilities";
import {
  serializeTimelineProject,
  timelineHasNonIdentityEdits,
  toPersistedTimelineProject,
  type PersistedTimelineProjectForWire,
} from "@/lib/timeline-serialize";
import EditorToolbar from "./EditorToolbar";
import VideoPreviewPanel from "./VideoPreviewPanel";
import TimelinePanel from "./TimelinePanel";
import ClipInspector from "./ClipInspector";
import TextOverlayInspector from "./TextOverlayInspector";
import MusicPickerDrawer from "./MusicPickerDrawer";
import LinkOverlayPickerSheet from "./LinkOverlayPickerSheet";
import "./TimelineEditor.css";

interface TimelineEditorProps {
  videoSrc: string;
  videoFile: File;
  videoDuration: number;
  videoWidth: number;
  videoHeight: number;
  initialProject?: TimelineProject;
  initialLinkOverlays?: OverlayItem[];
  autosave?: TimelineAutosaveOptions;
  onDone: (result: TimelineEditorResult) => void;
  onClose: () => void;
}

export interface TimelineEditorResult {
  project: TimelineProject;
  persistedProject: PersistedTimelineProjectForWire | null;
  hasTimelineEdits: boolean;
  exportedFile: File;
  linkOverlays: OverlayItem[];
}

export default function TimelineEditor({
  videoSrc,
  videoFile,
  videoDuration,
  videoWidth,
  videoHeight,
  initialProject,
  initialLinkOverlays,
  autosave,
  onDone,
  onClose,
}: TimelineEditorProps) {
  const project = useMemo(
    () =>
      initialProject ??
      createProjectFromVideo(videoSrc, videoDuration, videoWidth, videoHeight),
    [initialProject, videoSrc, videoDuration, videoWidth, videoHeight],
  );

  const capabilities = useTimelineCapabilities();
  const editor = useTimelineEditor(project, autosave, { maxClips: capabilities.maxClips });
  const [isExporting, setIsExporting] = useState(false);
  const [showMusicPicker, setShowMusicPicker] = useState(false);
  const [linkOverlays, setLinkOverlays] = useState<OverlayItem[]>(initialLinkOverlays ?? []);
  const [linkPickerKind, setLinkPickerKind] = useState<"mention" | "hashtag" | null>(null);
  const shellRef = useRef<HTMLDivElement>(null);

  const handleAddLinkOverlay = useCallback((item: OverlayItem) => {
    setLinkOverlays((prev) => {
      if (item.type === "mention") {
        const payload = item.payload as { username: string };
        const exists = prev.some(
          (o) => o.type === "mention" && (o.payload as { username: string }).username === payload.username,
        );
        if (exists) return prev;
      }
      if (item.type === "hashtag") {
        const payload = item.payload as { name: string };
        const exists = prev.some(
          (o) => o.type === "hashtag" && (o.payload as { name: string }).name === payload.name,
        );
        if (exists) return prev;
      }
      return [...prev, item];
    });
  }, []);

  const handleRemoveLinkOverlay = useCallback((id: string) => {
    setLinkOverlays((prev) => prev.filter((o) => o.id !== id));
  }, []);

  const handleMoveLinkOverlay = useCallback((id: string, x: number, y: number) => {
    setLinkOverlays((prev) => prev.map((o) => (o.id === id ? { ...o, x, y } : o)));
  }, []);

  const selectedOverlay = editor.selectedOverlayId
    ? editor.project.textOverlays.find((o) => o.id === editor.selectedOverlayId) ?? null
    : null;

  // Deferred upload-blob cleanup is owned by useTimelineEditor: its
  // dispatch queues orphaned blob keys for every delete action (no path
  // can bypass it) and flushes on unmount. We additionally flush at the
  // explicit lifecycle ends below (export/close) so blobs release promptly.

  // Clip selection and overlay selection are mutually exclusive in the UI:
  // only one inspector renders at a time, so picking one must clear the other.
  const handleSelectClip = useCallback(
    (id: string | null) => {
      editor.setSelectedClipId(id);
      if (id) editor.setSelectedOverlayId(null);
    },
    [editor],
  );

  const handleSelectOverlay = useCallback(
    (id: string | null) => {
      editor.setSelectedOverlayId(id);
      if (id) editor.setSelectedClipId(null);
    },
    [editor],
  );

  const handleExport = useCallback(async () => {
    if (isExporting) return;
    setIsExporting(true);

    try {
      editor.pause();
      const publishableProject = serializeTimelineProject(editor.project, capabilities);
      const hasTimelineEdits = timelineHasNonIdentityEdits(publishableProject);
      const persistedProject = hasTimelineEdits
        ? toPersistedTimelineProject(publishableProject)
        : null;
      // Undo history ends here — release blobs orphaned by deletions that
      // were never undone.
      editor.flushPendingBlobCleanup();
      // Draft cleanup is owned by the modal: it discards on successful publish
      // or on explicit Discard. Closing the editor with Done preserves the
      // draft so the user can resume if they back out of the publish step.
      onDone({
        project: publishableProject,
        persistedProject,
        hasTimelineEdits,
        exportedFile: videoFile,
        linkOverlays,
      });
    } catch (err) {
      console.error("Timeline save failed:", err);
    } finally {
      setIsExporting(false);
    }
  }, [isExporting, editor, capabilities, linkOverlays, videoFile, onDone]);

  const hasUnappliedChanges = editor.canUndo;

  const handleClose = useCallback(() => {
    if (
      hasUnappliedChanges &&
      !window.confirm("Close the editor? Your draft will stay saved.")
    ) {
      return;
    }
    // The draft was autosaved from the post-deletion project state and the
    // undo stack dies with the editor, so orphaned blobs are safe to release.
    editor.flushPendingBlobCleanup();
    onClose();
  }, [hasUnappliedChanges, onClose, editor]);

  // Split at playhead — find the clip under the playhead and split it.
  // Gated on maxClips: the serializer truncates clips past the cap at
  // publish, so allowing the split would silently lose content.
  const atClipCap =
    capabilities.maxClips > 0 && editor.project.clips.length >= capabilities.maxClips;

  const canSplit = useMemo(() => {
    if (atClipCap) return false;
    const hit = timeToClip(editor.project, editor.currentTime);
    if (!hit) return false;
    return editor.project.clips[hit.clipIndex].type === "video";
  }, [editor.project, editor.currentTime, atClipCap]);

  const handleSplit = useCallback(() => {
    if (atClipCap) return;
    const hit = timeToClip(editor.project, editor.currentTime);
    if (!hit) return;
    const clip = editor.project.clips[hit.clipIndex];
    if (clip.type !== "video") return;
    editor.dispatch({
      type: "SPLIT_CLIP",
      clipId: clip.id,
      atTime: hit.localTime,
    });
  }, [editor, atClipCap]);

  const handleAddTextOverlay = useCallback(() => {
    const totalDuration = getTotalDuration(editor.project);
    // Clamp the end to the timeline, but never collapse to zero duration:
    // when the playhead sits at the very end (a common parked state), a
    // start===end overlay never renders and shows a zero-width strip chip.
    const startTime = editor.currentTime;
    const clampedEnd = Math.min(startTime + 3, totalDuration);
    const endTime = clampedEnd > startTime ? clampedEnd : startTime + 0.5;
    const overlay: TimelineTextOverlay = {
      // Web renders overlays center-anchored; the factory records it so
      // the Expo renderers (top-left legacy default) place it correctly.
      ...createTextOverlay("center"),
      startTime,
      endTime,
      entryAnimation: "fade",
      exitAnimation: "fade",
    };
    editor.dispatch({ type: "ADD_TEXT_OVERLAY", overlay });
    editor.setSelectedOverlayId(overlay.id);
  }, [editor]);

  const handleDeleteSelection = useCallback(() => {
    if (editor.selectedOverlayId) {
      editor.dispatch({
        type: "DELETE_TEXT_OVERLAY",
        id: editor.selectedOverlayId,
      });
      editor.setSelectedOverlayId(null);
      return true;
    }

    if (!editor.selectedClip || editor.project.clips.length <= 1) {
      return false;
    }

    // Blob cleanup is deferred inside editor.dispatch, which queues the
    // clip's blobKey and releases it at flush time if nothing references it.
    editor.dispatch({ type: "DELETE_CLIP", clipId: editor.selectedClip.id });
    editor.setSelectedClipId(null);

    return true;
  }, [editor]);

  useEffect(() => {
    shellRef.current?.focus();
  }, []);

  useEffect(() => {
    const shell = shellRef.current;
    if (!shell) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const isTyping =
        target?.tagName === "INPUT" ||
        target?.tagName === "TEXTAREA" ||
        Boolean(target?.isContentEditable);

      if (event.key === "Escape" && !isTyping) {
        event.preventDefault();
        if (linkPickerKind) {
          setLinkPickerKind(null);
        } else if (showMusicPicker) {
          setShowMusicPicker(false);
        } else if (editor.selectedClipId) {
          editor.setSelectedClipId(null);
        } else if (editor.selectedOverlayId) {
          editor.setSelectedOverlayId(null);
        } else {
          handleClose();
        }
        return;
      }

      // Delete/Backspace is handled by useTimelineEditor's window-level
      // listener so it keeps working when focus escapes the shell (e.g.
      // after a drawer's close button unmounts and focus falls to body).

      if (event.key !== "Tab") return;

      // offsetParent is null for position:fixed elements (e.g. the music
      // picker drawer), which would silently exclude them from the trap.
      // getClientRects().length is a layout-based visibility check that
      // works for fixed and absolutely positioned focusables.
      const focusable = Array.from(
        shell.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((el) => el === document.activeElement || el.getClientRects().length > 0);

      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    shell.addEventListener("keydown", handleKeyDown);
    return () => shell.removeEventListener("keydown", handleKeyDown);
  }, [
    editor,
    editor.selectedClipId,
    editor.selectedOverlayId,
    handleDeleteSelection,
    handleClose,
    showMusicPicker,
    linkPickerKind,
  ]);

  return (
    <>
      <div
        className="timeline-editor-too-narrow"
        role="alertdialog"
        aria-labelledby="timeline-editor-too-narrow-title"
      >
        <div className="timeline-editor-too-narrow-content">
          <div
            className="timeline-editor-too-narrow-title"
            id="timeline-editor-too-narrow-title"
          >
            Wider window required
          </div>
          <div className="timeline-editor-too-narrow-body">
            The timeline editor needs at least 640px of width. Open Scroll on a
            larger screen or rotate your device.
          </div>
          <button
            type="button"
            className="scroll-btn-primary text-sm"
            onClick={handleClose}
          >
            Close editor
          </button>
        </div>
      </div>
    <div
      className="timeline-editor"
      role="dialog"
      aria-modal="true"
      aria-label="Timeline editor"
      aria-busy={isExporting}
      ref={shellRef}
      tabIndex={-1}
    >
      <EditorToolbar
        canUndo={editor.canUndo}
        canRedo={editor.canRedo}
        aspectRatio={editor.project.aspectRatio}
        isExporting={isExporting}
        lastSavedAt={editor.lastSavedAt}
        isAutosaving={editor.isAutosaving}
        onDispatch={editor.dispatch}
        onClose={handleClose}
        onExport={handleExport}
      />

      <div className="timeline-editor-shell">
        <aside className="timeline-tool-rail" aria-label="Timeline tools">
          <button
            className="timeline-tool-rail-btn"
            onClick={handleSplit}
            disabled={!capabilities.supportsSplit || !canSplit}
            title="Split at playhead (S)"
            aria-label="Split clip at playhead"
          >
            <Film size={18} />
            <span>Split</span>
          </button>
          <button
            className="timeline-tool-rail-btn"
            onClick={handleAddTextOverlay}
            title="Add text overlay"
            aria-label="Add text overlay"
          >
            <Type size={18} />
            <span>Text</span>
          </button>
          <button
            className="timeline-tool-rail-btn"
            onClick={() => setShowMusicPicker(true)}
            title="Add music or audio"
            aria-label="Add music or audio"
          >
            <Music size={18} />
            <span>Audio</span>
          </button>
          <button
            className="timeline-tool-rail-btn"
            onClick={() => setLinkPickerKind("mention")}
            title="Tag a person"
            aria-label="Tag a person"
          >
            <AtSign size={18} />
            <span>Mention</span>
          </button>
          <button
            className="timeline-tool-rail-btn"
            onClick={() => setLinkPickerKind("hashtag")}
            title="Add a hashtag"
            aria-label="Add a hashtag"
          >
            <Hash size={18} />
            <span>Hashtag</span>
          </button>
          <button
            className="timeline-tool-rail-btn"
            disabled
            title="Caption tools are coming to the web editor"
            aria-label="Caption tools unavailable"
          >
            <FileText size={18} />
            <span>Captions</span>
          </button>
        </aside>

        <div className="timeline-editor-workspace">
          <div className="timeline-editor-status" role="status" aria-live="polite">
            {isExporting ? "Preparing timeline..." : "Ready to publish"}
          </div>

          <div className="timeline-editor-body">
            <VideoPreviewPanel
              project={editor.project}
              activeOverlays={editor.activeOverlays}
              activeFilter={editor.activeFilter}
              activeClip={editor.activeClip}
              activeTransition={editor.activeTransition}
              currentTime={editor.currentTime}
              videoRef={editor.videoRef}
              linkOverlays={linkOverlays}
              onLinkOverlayMove={handleMoveLinkOverlay}
            />

            {selectedOverlay ? (
              <TextOverlayInspector
                overlay={selectedOverlay}
                onDispatch={editor.dispatch}
                onClose={() => editor.setSelectedOverlayId(null)}
                onDelete={handleDeleteSelection}
              />
            ) : editor.selectedClip ? (
              <ClipInspector
                clip={editor.selectedClip}
                clipCount={editor.project.clips.length}
                onDispatch={editor.dispatch}
                onClose={() => editor.setSelectedClipId(null)}
                onDelete={handleDeleteSelection}
                capabilities={capabilities}
              />
            ) : null}
          </div>

          <TimelinePanel
            project={editor.project}
            currentTime={editor.currentTime}
            totalDuration={editor.totalDuration}
            isPlaying={editor.isPlaying}
            selectedClipId={editor.selectedClipId}
            selectedOverlayId={editor.selectedOverlayId}
            onTogglePlayback={editor.togglePlayback}
            onSeekTo={editor.seekTo}
            onSelectClip={handleSelectClip}
            onSelectOverlay={handleSelectOverlay}
            onDispatch={editor.dispatch}
            onPause={editor.pause}
            onAddAudio={() => setShowMusicPicker(true)}
            capabilities={capabilities}
          />
        </div>
      </div>

      <div className="sr-only" aria-live="polite">
        {editor.isPlaying ? "Timeline playback started" : "Timeline playback paused"}
      </div>

      {showMusicPicker && (
        <MusicPickerDrawer
          project={editor.project}
          onDispatch={editor.dispatch}
          onClose={() => setShowMusicPicker(false)}
        />
      )}

      {linkPickerKind && (
        <LinkOverlayPickerSheet
          kind={linkPickerKind}
          existing={linkOverlays}
          onAdd={handleAddLinkOverlay}
          onRemove={handleRemoveLinkOverlay}
          onClose={() => setLinkPickerKind(null)}
        />
      )}
    </div>
    </>
  );
}

export { TimelineEditor };
